import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import { showLoading } from 'react-global-loading';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../utils/authStorage';
import messages from '../../../../assets/Messages.json';
import Remarks, { validateRemarksSection } from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';
import { confirmAndDelete } from '../../../../utils/confirmAndDelete';

function ProfitAndLossAccount() {
  const navigate = useNavigate();

  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);
  const saveInProgressRef = useRef(false);

  const {
    blockItemData,
    filteredBlockItems,
    isLoadingBlockItems,
    debouncedBlockItemSearch,
    loadMoreBlockItemData,
    hasMoreBlockItems,
    isLoadingMoreBlockItems,
  } = useASIItemMaster();

  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  const [formData, setFormData] = useState({
    items: Array(100).fill(''),
    otherItems: Array(100).fill(''),
    closingData: Array(100).fill(''),
    status: Array(100).fill('')
  });

  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });

  const [remarksList, setRemarksList] = useState([]);
  const [visibleRows, setVisibleRows] = useState(1);
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({});
  const dropdownItemRefs = useRef({});
  const justOpenedRef = useRef({});
  const [profitLossItems, setProfitLossItems] = useState([]);
  const [filteredProfitLossItems, setFilteredProfitLossItems] = useState([]);
  const [isLoadingProfitLossItems, setIsLoadingProfitLossItems] = useState(false);
  const [rowApiMeta, setRowApiMeta] = useState(() => Array(100).fill(null).map(() => ({})));

  const getAuthConfig = () => {
    if (!hasAuthSession()) {
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return { headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' } };
    }
    return getAuthHeaders();
  };

  useEffect(() => {
    dataLoadedRef.current = false;
    isLoadingRef.current = false;
    apiCallMadeRef.current = false;
  }, []);

  useEffect(() => {
    const fetchHeaderData = () => {
      try {
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);

          setHeaderData({
            despatchSlNo: parsedData.dslNo || '',
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: parsedData.dslNo || '',
            surveyYear: parsedData.year || ''
          });
        } else {
          console.log('No balanceSheet data found in localStorage');
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
        }
      } catch (error) {
        console.error('Error fetching data from localStorage:', error);
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
      }
    };

    fetchHeaderData();
  }, []);

  useEffect(() => {
    const shouldLoadData = !dataLoadedRef.current &&
      !isLoadingRef.current &&
      !apiCallMadeRef.current &&
      headerData.despatchSlNo &&
      headerData.despatchSlNo !== 'DSL No. not set' &&
      headerData.despatchSlNo !== 'Error loading data' &&
      headerData.surveyYear &&
      headerData.surveyYear.trim() !== '';

    console.log('=== ProfitAndLossAccount API Call Check ===');
    console.log('dataLoadedRef.current:', dataLoadedRef.current);
    console.log('isLoadingRef.current:', isLoadingRef.current);
    console.log('apiCallMadeRef.current:', apiCallMadeRef.current);
    console.log('headerData.despatchSlNo:', headerData.despatchSlNo);
    console.log('headerData.surveyYear:', headerData.surveyYear);
    console.log('shouldLoadData:', shouldLoadData);
    // console.log('==========================================');

    if (shouldLoadData) {
      if (hasAuthSession()) {
        console.log('✅ Triggering loadExistingData - all conditions met');
        console.log('DSL No:', headerData.despatchSlNo);
        console.log('Survey Year:', headerData.surveyYear);
        apiCallMadeRef.current = true;
        loadExistingData();
      } else {
        console.log('❌ No auth session found, skipping data load');
        apiCallMadeRef.current = false;
      }
    } else {
      console.log('❌ Conditions not met for API call');
      if (dataLoadedRef.current) console.log('  - Data already loaded');
      if (isLoadingRef.current) console.log('  - Already loading');
      if (apiCallMadeRef.current) console.log('  - API call already made');
      if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data') console.log('  - DSL No. not available');
      if (!headerData.surveyYear || headerData.surveyYear.trim() === '') console.log('  - Survey Year not available');
    }
  }, [headerData.despatchSlNo, headerData.surveyYear]);
  


  useEffect(() => {
    const timer = setTimeout(() => {
      if (!dataLoadedRef.current && !isLoadingRef.current && !apiCallMadeRef.current) {
        console.log('Fallback: Triggering loadExistingData after timeout');
        if (hasAuthSession() && headerData.despatchSlNo && headerData.despatchSlNo !== 'DSL No. not set') {
          loadExistingData();
        }
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [headerData.despatchSlNo]);

  const toggleDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = {};
      const isCurrentlyOpen = prev[rowIndex] || false;
      newState[rowIndex] = !isCurrentlyOpen;
      return newState;
    });
    setSelectedIndex(prev => {
      const currentState = prev[rowIndex];
      if (currentState === undefined || currentState >= 0) {
        return { ...prev, [rowIndex]: -1 };
      }
      return prev;
    });
  };

  const openDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      if (prev[rowIndex]) {
        return prev;
      }
      const newState = {};
      newState[rowIndex] = true;
      justOpenedRef.current[rowIndex] = true;
      setTimeout(() => {
        justOpenedRef.current[rowIndex] = false;
      }, 300);
      return newState;
    });
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  const shouldOpenUpward = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return false;

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    return spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  };

  const getDropdownPosition = (rowIndex) => {
    const inputElement = document.querySelector(`input[data-row="${rowIndex}"]`);
    if (!inputElement) return { top: '100%', bottom: 'auto' };

    const rect = inputElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200;

    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return { top: 'auto', bottom: '100%' };
    } else {
      return { top: '100%', bottom: 'auto' };
    }
  };

  const getDropdownStyle = (rowIndex) => {
    const position = getDropdownPosition(rowIndex);
    const isUpward = shouldOpenUpward(rowIndex);

    return {
      ...position,
      left: 0,
      right: 0,
      zIndex: 99999,
      maxHeight: '200px',
      overflowY: 'auto',
      overflowX: 'hidden',
      minWidth: '250px',
      width: 'max-content',
      maxWidth: '400px',
      border: '1px solid #ddd',
      boxShadow: isUpward ? '0 -4px 6px rgba(0, 0, 0, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.1)',
      position: 'absolute',
      marginTop: isUpward ? '0' : '2px',
      marginBottom: isUpward ? '2px' : '0',
      transform: 'translateY(0)',
      willChange: 'transform',
      scrollbarWidth: 'thin',
      scrollbarColor: '#ccc #f1f1f1',
      backgroundColor: 'white',
      borderRadius: '4px'
    };
  };

  const closeDropdown = (rowIndex) => {
    setDropdownStates(prev => {
      const newState = { ...prev };
      newState[rowIndex] = false;
      return newState;
    });
    setSelectedIndex(prev => ({ ...prev, [rowIndex]: -1 }));
  };

  const closeAllDropdowns = () => {
    setDropdownStates({});
    setSelectedIndex({});
  };

  const handleSearchChange = (rowIndex, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [rowIndex]: value
    }));
    debouncedBlockItemSearch(value);
    if (value.trim()) {
      const filtered = profitLossItems.filter(item =>
        (item.item_name || '').toLowerCase().includes(value.toLowerCase()) ||
        (item.block_item || '').toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProfitLossItems(filtered);
    } else {
      setFilteredProfitLossItems(profitLossItems);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.position-relative')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleItemChange = (index, selectedItem) => {
    const newItems = [...formData.items];
    const newStatus = [...formData.status];

    if (selectedItem) {
      newItems[index] = selectedItem.item_name || '';
      newStatus[index] = selectedItem.block_item || '';

      const blockInfo = getBlockInfo(selectedItem.block_item || '');
      setRowApiMeta(prev => {
        const next = [...prev];
        next[index] = {
          assetCode: selectedItem.assetCode ?? selectedItem.asset_code ?? 0,
          itemCode: selectedItem.itemCode ?? selectedItem.item_code ?? blockInfo.itemCode ?? 0,
          blockId: selectedItem.blockId ?? selectedItem.block_id ?? blockInfo.blockId ?? 'G'
        };
        return next;
      });

      if (index === visibleRows - 1 && visibleRows < 100) {
        setVisibleRows(visibleRows + 1);
      }
    } else {
      newItems[index] = '';
      newStatus[index] = '';
      setRowApiMeta(prev => {
        const next = [...prev];
        next[index] = {};
        return next;
      });
    }

    setFormData({
      ...formData,
      items: newItems,
      status: newStatus
    });

    closeDropdown(index);
  };

  const handleKeyDown = (e, rowIndex, filteredItemsList) => {
    const currentSelected = selectedIndex[rowIndex] !== undefined ? selectedIndex[rowIndex] : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!dropdownStates[rowIndex]) {
          openDropdown(rowIndex);
        }
        if (currentSelected < filteredItemsList.length - 1) {
          const newIndex = currentSelected + 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        if (!dropdownStates[rowIndex]) {
          openDropdown(rowIndex);
        }
        if (currentSelected > 0) {
          const newIndex = currentSelected - 1;
          setSelectedIndex(prev => ({ ...prev, [rowIndex]: newIndex }));
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${rowIndex}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (dropdownStates[rowIndex] && currentSelected >= 0 && currentSelected < filteredItemsList.length) {
          handleItemChange(rowIndex, filteredItemsList[currentSelected]);
        } else if (!dropdownStates[rowIndex]) {
          openDropdown(rowIndex);
        }
        break;

      case 'Escape':
        e.preventDefault();
        closeDropdown(rowIndex);
        break;

      case 'Tab':
        closeDropdown(rowIndex);
        break;

      default:
        if (!dropdownStates[rowIndex] && e.key.length === 1) {
          openDropdown(rowIndex);
        }
        break;
    }
  };

  const removeRowFromState = (index) => {
    const newItems = [...formData.items];
    const newOtherItems = [...formData.otherItems];
    const newClosingData = [...formData.closingData];
    const newStatus = [...formData.status];

    newItems.splice(index, 1);
    newOtherItems.splice(index, 1);
    newClosingData.splice(index, 1);
    newStatus.splice(index, 1);

    newItems.push('');
    newOtherItems.push('');
    newClosingData.push('');
    newStatus.push('');

    setFormData({
      items: newItems,
      otherItems: newOtherItems,
      closingData: newClosingData,
      status: newStatus
    });

    setRowApiMeta(prev => {
      const next = [...prev];
      next.splice(index, 1);
      next.push({});
      return next;
    });

    if (index < visibleRows) {
      setVisibleRows(prev => Math.max(1, prev - 1));
    }
    closeAllDropdowns();
  };

  const handleDeleteSingleRow = async (index) => {
    const itemName = formData.items[index] || '';
    const message = `Are you sure you want to delete Row ${index + 1}?${itemName ? `\n\nItem: ${itemName}` : ''}`;

    const blockInfo = getBlockInfo(formData.status[index] || '');
    const surveyYearOnly = (headerData.surveyYear || '').split('-')[0] || '';
    const value = formData.closingData[index] !== undefined && formData.closingData[index] !== '' ? parseFloat(formData.closingData[index]) || 0 : 0;
    const meta = rowApiMeta[index] || {};
    const matchingItem = profitLossItems.find(item => (item.item_name || '') === itemName);
    const assetCodeFromMeta = meta.assetCode ?? meta.asset_code;
    const assetCodeFallback = matchingItem?.asset_code ?? matchingItem?.assetCode ?? 0;
    const assetCodeNum = typeof assetCodeFromMeta === 'number' ? assetCodeFromMeta : (parseFloat(assetCodeFromMeta) ?? (typeof assetCodeFallback === 'number' ? assetCodeFallback : parseFloat(assetCodeFallback) || 0));
    const itemCodeNum = meta.itemCode ?? meta.item_code ?? blockInfo.itemCode ?? 0;
    const blockIdForPayload = meta.blockId ?? meta.block_id ?? blockInfo.blockId ?? 'G';

    const deleted = await confirmAndDelete({
      message,
      showLoadingDuringCall: true,
      successMessage: `Row ${index + 1} item delete successfully`,
      deleteApiCall: async () => {
        const payload = {
          dsl_no: headerData.despatchSlNo || headerData.dslNo || '',
          survey_year: surveyYearOnly,
          block_type: blockIdForPayload,
          block_title: 'Profit And Loss Account',
          row_index: index,
          item_name: itemName,
          assetCode: assetCodeNum,
          itemCode: typeof itemCodeNum === 'number' ? itemCodeNum : (parseInt(itemCodeNum, 10) || 0),
          value
        };
        return axios.post(`${API_URL}api/balanceSheet/delete-item`, payload, getAuthConfig());
      },
      onSuccess: () => removeRowFromState(index),
    });

    if (!deleted) return;
  };

  const handleInputChange = (index, field, value) => {
    const newFormData = { ...formData };
    let validatedValue = value;

    if (field === 'closingData') {
      if (value === '' || value === '0' || /^\d*\.?\d*$/.test(value)) {
        if (value.includes('.')) {
          const parts = value.split('.');
          if (parts[1] && parts[1].length > 2) {
            validatedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }
        if (validatedValue.length > 15) {
          validatedValue = validatedValue.substring(0, 15);
        }
      } else {
        return;
      }
    }

    newFormData[field][index] = validatedValue;
    setFormData(newFormData);
  };

  const getBlockInfo = (status) => {
    if (!status || status === '') {
      return { blockId: 'G', blockType: 'ASI_BLOCKG', itemCode: 1 };
    }
    const blockMatch = status.match(/Block-([A-Z]),\s*Item-(\d+)/i);
    if (blockMatch) {
      const blockLetter = blockMatch[1];
      const itemCode = parseInt(blockMatch[2]);
      return {
        blockId: blockLetter,
        blockType: `ASI_BLOCK${blockLetter}`,
        itemCode: itemCode
      };
    }
    const altMatch = status.match(/Block ([A-Z]),\s*Item-(\d+)/i);
    if (altMatch) {
      const blockLetter = altMatch[1];
      const itemCode = parseInt(altMatch[2]);
      return {
        blockId: blockLetter,
        blockType: `ASI_BLOCK${blockLetter}`,
        itemCode: itemCode
      };
    }
    const blockOnlyMatch = status.match(/Block-([A-Z])/i);
    if (blockOnlyMatch) {
      const blockLetter = blockOnlyMatch[1];
      return {
        blockId: blockLetter,
        blockType: `ASI_BLOCK${blockLetter}`,
        itemCode: 1
      };
    }
    return { blockId: 'G', blockType: 'ASI_BLOCKG', itemCode: 1 };
  };

  const fetchProfitLossItems = async () => {
    setIsLoadingProfitLossItems(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        throw new Error('Authentication token not found');
      }

      console.log('Fetching Profit & Loss items from API...');
      const response = await axios.get(`${API_URL}api/asi/balanceSheetBlockItemsByTypes`, currentConfig);

      if (response.data && response.data.status && response.data.data) {
        const data = response.data.data;
        console.log(`Loaded ${data.length} Profit & Loss items from API`);
        const sortedData = data.sort((a, b) =>
          (a.item_name || '').localeCompare(b.item_name || '')
        );

        setProfitLossItems(sortedData);
        setFilteredProfitLossItems(sortedData);
      } else {
        throw new Error(response.data.message || 'Failed to load Profit & Loss items');
      }
    } catch (error) {
      console.error('Error fetching Profit & Loss items:', error);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions.');
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.');
      } else {
        toast.error('Error loading Profit & Loss items. Please try again.');
      }
    } finally {
      setIsLoadingProfitLossItems(false);
    }
  };

  const loadExistingData = async () => {
    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set' || headerData.despatchSlNo === 'Error loading data' || !headerData.surveyYear || headerData.surveyYear.trim() === '') {
      console.log('❌ loadExistingData: Missing required data - DSL No. or Survey Year');
      console.log('DSL No:', headerData.despatchSlNo);
      console.log('Survey Year:', headerData.surveyYear);
      apiCallMadeRef.current = false;
      return;
    }

    if (isLoadingRef.current) {
      console.log('loadExistingData: Already loading, skipping...');
      return;
    }
    isLoadingRef.current = true;
    showLoading(true);

    try {
      const payload = {
        dsl_no: headerData.despatchSlNo,
        survey_year: headerData.surveyYear.split('-')[0]
      };

      console.log('Loading existing data with payload:', payload);

      const [blockResponse, profitLossResponse] = await Promise.all([
        axios.post(`${API_URL}api/block/get-data`, payload, getAuthConfig()),
        axios.get(`${API_URL}api/asi/balanceSheetBlockItemsByTypes`, getAuthConfig())
      ]);

      console.log('Block API Response:', blockResponse.data);
      console.log('Profit Loss Items API Response:', profitLossResponse.data);

      if (blockResponse.data && blockResponse.data.status && blockResponse.data.data) {
        const existingData = blockResponse.data.data;
        const newFormData = {
          items: Array(100).fill(''),
          otherItems: Array(100).fill(''),
          closingData: Array(100).fill(''),
          status: Array(100).fill('')
        };
        const newRowApiMeta = Array(100).fill(null).map(() => ({}));
        let availableProfitLossItems = [];
        if (profitLossResponse.data && profitLossResponse.data.status && profitLossResponse.data.data) {
          const profitLossData = profitLossResponse.data.data;
          console.log(`Loaded ${profitLossData.length} Profit & Loss items from API`);

          const sortedData = profitLossData.sort((a, b) =>
            (a.item_name || '').localeCompare(b.item_name || '')
          );

          availableProfitLossItems = sortedData;
          setProfitLossItems(sortedData);
          setFilteredProfitLossItems(sortedData);
        }

        existingData.forEach((item, index) => {
          if (index < 100) {
            const itemName = item.itemName || item.item_name || '';
            const matchingItem = availableProfitLossItems.find(dropdownItem =>
              dropdownItem.item_name === itemName
            );

            if (matchingItem) {
              newFormData.items[index] = matchingItem.item_name || '';
              newFormData.status[index] = item.blockItem || item.block_item || matchingItem.block_item || '';
            } else {
              newFormData.items[index] = itemName;
              newFormData.status[index] = item.blockItem || item.block_item || '';
            }

            newFormData.otherItems[index] = item.otherItem || item.other_item || '';
            const value = item.value !== undefined && item.value !== null ? item.value : '';
            newFormData.closingData[index] = value.toString();

            newRowApiMeta[index] = {
              assetCode: item.assetCode ?? item.asset_code ?? 0,
              itemCode: item.itemCode ?? item.item_code ?? 0,
              blockId: item.blockId ?? item.block_id ?? (getBlockInfo(item.blockItem || item.block_item || '').blockId || 'G')
            };
          }
        });

        setRowApiMeta(newRowApiMeta);

        let mappedRemarksList = [];
        if (blockResponse.data.remarks && Array.isArray(blockResponse.data.remarks)) {
          mappedRemarksList = blockResponse.data.remarks.map((remark, index) => {
            let blockLetter = '';
            if (remark.block) {
              blockLetter = remark.block.replace(/^BLOCK\s*[-]?\s*/i, '').trim().toUpperCase();
            }
            let formattedDate = '';
            const dateValue = remark.createdDate || remark.created_date || remark.updatedDate || remark.updated_date || '';
            if (dateValue) {
              try {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                }
              } catch (e) {
                formattedDate = dateValue;
              }
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columnsName || remark.columns_name || remark.columns || '',
              items: remark.items || remark.Items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: formattedDate || dateValue || '',
              remarksBy: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || '',
              role: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || ''
            };
          });
          setRemarksList(mappedRemarksList);
          console.log('Mapped remarks list:', mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        setRemarksData({
          remarksBlock: '',
          remarksItems: '',
          remarks: ''
        });

        setFormData(newFormData);
        const filledRows = existingData.length;
        setVisibleRows(Math.max(1, Math.min(100, filledRows + 1)));

        dataLoadedRef.current = true;
      }
    } catch (error) {
      console.error('Error loading existing data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        console.log('No existing data found');
      } else {
        toast.error('Error loading data. Please try again.', messages.COLOR_RED);
      }
    } finally {
      isLoadingRef.current = false;
      showLoading(false);
    }
  };

  const handleSaveToAPI = async () => {
    if (saveInProgressRef.current) {
      console.log('Save already in progress, skipping...');
      return false;
    }

    const remarksValidation = validateRemarksSection(remarksData);
    if (!remarksValidation.valid) {
      toast.error(remarksValidation.message, messages.COLOR_RED);
      return false;
    }

    if (!headerData.despatchSlNo || headerData.despatchSlNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    if (!hasAuthSession()) {
      toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
      return false;
    }

    console.log('Starting save process...');
    console.log('DSL No:', headerData.despatchSlNo);

    saveInProgressRef.current = true;
    showLoading(true);

    try {
      const dataToSave = [];

      for (let i = 0; i < visibleRows; i++) {
        const item = {
          item_name: formData.items[i] || '',
          other_item: formData.otherItems[i] || '',
          value: formData.closingData[i] || '',
          block_item: formData.status[i] || ''
        };

        if (item.item_name || item.value || item.other_item) {
          const blockInfo = getBlockInfo(item.block_item);
          console.log(`Row ${i}: Status="${item.block_item}" -> BlockId="${blockInfo.blockId}", BlockType="${blockInfo.blockType}", ItemCode="${blockInfo.itemCode}"`);

          const payload = {
            dsl_no: headerData.despatchSlNo,
            survey_year: headerData.surveyYear.split('-')[0] || '2022',
            block_type: blockInfo.blockType,
            item_code: blockInfo.itemCode,
            asset_code: "",
            item_name: item.item_name,
            other_item: item.other_item || '',
            BLOCK_ID: blockInfo.blockId,
            BLOCK_ITEM: item.block_item || `Block-${blockInfo.blockId},Item-${blockInfo.itemCode}`,
            value: item.value ? parseFloat(item.value) : 0
          };

          dataToSave.push(payload);
        }
      }

      if (dataToSave.length === 0) {
        toast.warning('No data to save', messages.COLOR_RED);
        return false;
      }

      const remarksArray = [];
      const hasRemarksText = remarksData.remarks && remarksData.remarks.trim() !== '';
      const hasRemarksBlock = remarksData.remarksBlock && remarksData.remarksBlock !== 'SELECT_REMARKS' && remarksData.remarksBlock !== 'ALL';
      const hasRemarksItems = remarksData.remarksItems && remarksData.remarksItems.trim() !== '';

      if (hasRemarksText || hasRemarksBlock || hasRemarksItems) {
        let blockLetter = '';
        if (hasRemarksBlock) {
          const blockMatch = remarksData.remarksBlock.match(/Block-([A-Z])/i);
          if (blockMatch) {
            blockLetter = blockMatch[1];
          } else {
            const selectedItem = profitLossItems.find(item => item.item_name === remarksData.remarksBlock);
            if (selectedItem && selectedItem.block_item) {
              const itemBlockMatch = selectedItem.block_item.match(/Block-([A-Z])/i);
              if (itemBlockMatch) {
                blockLetter = itemBlockMatch[1];
              }
            }
          }
        }

        remarksArray.push({
          dsl_no: headerData.despatchSlNo,
          survey_year: headerData.surveyYear.split('-')[0] || '2022',
          block: blockLetter || '',
          columnsName: remarksData.remarksItems || '',
          items: hasRemarksBlock ? remarksData.remarksBlock : '',
          remarks: remarksData.remarks || ''
        });
      }

      const finalPayload = {
        items: dataToSave,
        remarks: remarksArray
      };

      console.log('Sending data to API:', finalPayload);
      console.log('API URL:', `${API_URL}api/block/insert-item`);
      console.log('Dynamic block mapping applied for each row');

      if (!hasAuthSession()) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return false;
      }

      const authConfig = getAuthHeaders();
      console.log('Auth headers:', authConfig);

      const requestInterceptor = axios.interceptors.request.use(
        (config) => {
          console.log('Request interceptor - Headers being sent:', config.headers);
          console.log('Request interceptor - Authorization header:', config.headers?.Authorization);
          return config;
        },
        (error) => {
          console.error('Request interceptor error:', error);
          return Promise.reject(error);
        }
      );

      const response = await axios.post(`${API_URL}api/block/insert-item`, finalPayload, authConfig);
      axios.interceptors.request.eject(requestInterceptor);

      console.log('API Response:', response.data);

      if (response.data && response.data.status) {
        toast.success('Data saved successfully!', messages.COLOR_RED);
        markPageAsSaved(PAGE_NAMES.PROFIT_AND_LOSS);
        if (headerData.despatchSlNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.despatchSlNo, headerData.surveyYear);
        }

        return true;
      } else {
        toast.error('Failed to save data', messages.COLOR_RED);
        return false;
      }
    } catch (error) {
      console.error('Error saving data:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 403) {
        toast.error('Access denied. Please check your permissions or contact administrator.', messages.COLOR_RED);
        console.error('403 Error Details:', error.response?.data);
      } else if (error.response?.status === 400) {
        toast.error('Invalid data. Please check your inputs.', messages.COLOR_RED);
        console.error('400 Error Details:', error.response?.data);
      } else {
        toast.error(`Error saving data: ${error.message}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      saveInProgressRef.current = false;
      showLoading(false);
    }
  };

  const generateTableRows = () => {
    const rows = [];
    const currentFilteredItems = filteredProfitLossItems.length > 0 ? filteredProfitLossItems : profitLossItems;
    const selectedItems = new Set(formData.items.filter(item => item && item.trim() !== ''));

    for (let i = 0; i < visibleRows; i++) {
      const availableItems = currentFilteredItems.filter(item => {
        const isAlreadySelected = selectedItems.has(item.item_name);
        const isCurrentRowItem = formData.items[i] === item.item_name;
        return !isAlreadySelected || isCurrentRowItem;
      });

      rows.push(
        <tr key={i}>
          <td style={{ textAlign: 'center', fontWeight: '500' }}>{i + 1}</td>
          <td>
            <div className="position-relative" style={{ position: 'relative', overflow: 'visible' }}>
              <input
                type="text"
                className="form-control"
                value={formData.items[i] || ''}
                placeholder={isLoadingProfitLossItems ? 'Loading...' : 'Select Item'}
                data-row={i}
                style={{
                  textAlign: 'center',
                  paddingRight: '30px',
                  fontSize: '12px',
                  minHeight: '32px',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}
                onChange={(e) => {
                  handleSearchChange(i, e.target.value);
                  const newItems = [...formData.items];
                  newItems[i] = e.target.value;
                  setFormData({ ...formData, items: newItems });
                  setSelectedIndex(prev => ({ ...prev, [i]: -1 }));
                }}
                onFocus={(e) => {
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!justOpenedRef.current[i]) {
                    toggleDropdown(i);
                  } else {
                    justOpenedRef.current[i] = false;
                  }
                }}
                onMouseDown={(e) => {
                  if (!dropdownStates[i]) {
                    openDropdown(i);
                  }
                }}
                onKeyDown={(e) => {
                  handleKeyDown(e, i, availableItems);
                }}
                disabled={isLoadingProfitLossItems}
              />
              <i className="fa fa-chevron-down position-absolute"
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  pointerEvents: 'none'
                }}></i>
              {dropdownStates[i] && (
                <div
                  className="position-absolute bg-white border rounded shadow-sm custom-dropdown"
                  style={getDropdownStyle(i)}
                >
                  {availableItems.length > 0 ? (
                    <>
                      {availableItems.map((singleItem, index) => {
                        const isSelected = selectedIndex[i] === index;
                        return (
                          <div
                            key={index}
                            ref={(el) => {
                              if (el) {
                                dropdownItemRefs.current[`${i}-${index}`] = el;
                              }
                            }}
                            className="px-3 py-2 cursor-pointer"
                            style={{
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '12px',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              minHeight: '40px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'center',
                              backgroundColor: isSelected ? '#e3f2fd' : 'white',
                              outline: isSelected ? '2px solid #1976d2' : 'none',
                              outlineOffset: '-2px'
                            }}
                            onClick={() => handleItemChange(i, singleItem)}
                            onMouseEnter={(e) => {
                              e.target.style.backgroundColor = '#f8f9fa';
                              setSelectedIndex(prev => ({ ...prev, [i]: index }));
                            }}
                            onMouseLeave={(e) => {
                              if (!isSelected) {
                                e.target.style.backgroundColor = 'white';
                              }
                            }}
                            tabIndex={-1}
                          >
                            <div style={{
                              fontWeight: 'bold',
                              marginBottom: '2px',
                              wordBreak: 'break-word',
                              lineHeight: '1.3'
                            }}>
                              {singleItem.item_name}
                            </div>
                            {singleItem.block_item && (
                              <div style={{
                                color: '#666',
                                fontSize: '11px',
                                wordBreak: 'break-word',
                                lineHeight: '1.2'
                              }}>
                                {singleItem.block_item}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <div className="px-3 py-2 text-muted" style={{ fontSize: '12px' }}>
                      {isLoadingProfitLossItems ? 'Loading items...' : 'No items available'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </td>
          <td>
            <input
              type="text"
              className="form-control"
              value={formData.otherItems[i] || ''}
              onChange={(e) => handleInputChange(i, 'otherItems', e.target.value)}
              maxLength="200"
              disabled style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '12px', minHeight: '32px' }}
            />
          </td>
          <td><input
            type="text"
            className="form-control"
            value={formData.closingData[i]}
            onChange={(e) => handleInputChange(i, 'closingData', e.target.value)}
            maxLength="15"
            pattern="[0-9]*\.?[0-9]*"
            disabled={!formData.items[i] || formData.items[i].trim() === ''}
            style={{
              fontSize: '12px',
              minHeight: '32px',
              textAlign: 'center',
              backgroundColor: (!formData.items[i] || formData.items[i].trim() === '') ? '#f5f5f5' : '#fff',
              cursor: (!formData.items[i] || formData.items[i].trim() === '') ? 'not-allowed' : 'text'
            }}
          /></td>
          <td>
            <input
              type="text"
              className="form-control"
              value={formData.status[i]}
              disabled style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed', fontSize: '12px', minHeight: '32px' }}
            />
          </td>
          <td>
            {(i !== 0 && i !== visibleRows - 1) && (
              <a
                href="#"
                className='d-block text-center'
                onClick={(e) => {
                  e.preventDefault();
                  handleDeleteSingleRow(i);
                }}
                title={`Delete Row ${i + 1}`}
              >
                <i className="fa fa-trash"></i>
              </a>
            )}
          </td>
        </tr>
      );
    }
    return rows;
  };

  const filteredRemarksList = useMemo(() => {
    return filterRemarksList(remarksList, remarksData);
  }, [remarksList, remarksData]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <style>
        {`
          .custom-dropdown::-webkit-scrollbar {
            width: 6px;
          }
          .custom-dropdown::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 3px;
          }
          .custom-dropdown::-webkit-scrollbar-thumb {
            background: #ccc;
            border-radius: 3px;
          }
          .custom-dropdown::-webkit-scrollbar-thumb:hover {
            background: #999;
          }

          /* Print Styles */
          @media print {
            @page {
              size: A4 landscape;
              margin: 0.5cm;
            }

            body {
              margin: 0;
              padding: 0;
            }

            .container-fluid {
              padding: 0 !important;
              margin: 0 !important;
            }

            .card {
              margin: 0 !important;
              box-shadow: none !important;
              border: none !important;
            }

            /* Hide action buttons during print */
            .btn_orange,
            button[type="button"],
            hr {
              display: none !important;
            }

            /* Ensure table fits on page */
            table {
              width: 100% !important;
              font-size: 9px !important;
              page-break-inside: auto !important;
            }

            thead {
              display: table-header-group !important;
            }

            tbody {
              display: table-row-group !important;
            }

            tr {
              page-break-inside: avoid !important;
              page-break-after: auto !important;
            }

            th, td {
              padding: 4px 2px !important;
              border: 1px solid #000 !important;
              word-wrap: break-word !important;
              overflow-wrap: break-word !important;
            }

            th {
              font-size: 8px !important;
              font-weight: bold !important;
              background-color: #f0f0f0 !important;
            }

            td {
              font-size: 8px !important;
            }

            /* Adjust column widths for print */
            table th:nth-child(1),
            table td:nth-child(1) {
              width: 35% !important;
              min-width: 200px !important;
            }

            table th:nth-child(2),
            table td:nth-child(2) {
              width: 20% !important;
              min-width: 120px !important;
            }

            table th:nth-child(3),
            table td:nth-child(3) {
              width: 25% !important;
              min-width: 150px !important;
            }

            /* Hide dropdown elements during print */
            .fa-chevron-down,
            .position-absolute:not(.print-visible),
            .custom-dropdown {
              display: none !important;
            }

            /* Ensure inputs are readable */
            input.form-control {
              border: none !important;
              background: transparent !important;
              padding: 0 !important;
              font-size: 8px !important;
              text-align: right !important;
            }

            input.form-control:disabled {
              background: transparent !important;
            }
          }
        `}
      </style>
      <div className="container-fluid py-3">
        {/* Header Section - Direct Display */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-12 mb-3">
                <h4 className="page_heading mb-3 pb-2 border-bottom">
                  ASI Schedule 2024-25 <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>CONFIDENTIAL <i className="fa fa-lock me-2" style={{ fontSize: '20px', color: '#1976d2' }}></i></span>
                </h4>
                <h5 className="mb-0 pb-3 border-bottom"><span style={{ color: '#1976d2', fontWeight: 'bold' }}>Profit and Loss Account</span> <strong>Data for Block E, F, G, H, I, J</strong></h5>
              </div>
              <div className="row">
                <div className="col-md-8">
                  <div className="row g-3">
                    <div className="col-md-4">
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                        <strong>DSL No.:</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {headerData.despatchSlNo}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                        <strong>PSL No.:</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {headerData.permanentSlNo}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div style={{ fontSize: '14px', fontWeight: '500', color: '#333', marginBottom: '6px' }}>
                        <strong>Name of Company:</strong>
                      </div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {headerData.companyName}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Table Styles */}
        <style>
          {`
            .table th {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              font-weight: 600;
              font-size: 14px;
              text-align: center;
              vertical-align: middle;
              padding: 8px 4px;
            }
            .table td {
              border: 1px solid #dee2e6;
              padding: 6px 4px;
              font-size: 13px;
            }
            .table input {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
            }
            .table input:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .table input:disabled {
              background-color: #f8f9fa;
              border-color: #dee2e6;
              color: #6c757d;
              cursor: not-allowed;
            }
            .table select {
              border: 1px solid #e0e0e0;
              background: white;
              width: 100%;
              text-align: center;
              font-size: 13px;
              border-radius: 6px;
              padding: 8px 12px;
              transition: all 0.3s ease;
              padding-right: 35px;
            }
            .table select:focus {
              outline: none;
              border-color: #f75226;
              transform: translateY(-1px);
            }
            .status-field {
              background-color: #f8f9fa;
              border: 1px solid #dee2e6;
              padding: 6px 8px;
              font-size: 12px;
              text-align: center;
              min-width: 120px;
            }
          `}
        </style>

        {/* Main Content Area */}
        <div className="card mb-3" style={{ position: 'relative', zIndex: 1 }}>
          <div className="card-body" style={{ overflow: 'visible', position: 'relative', paddingBottom: '50px' }}>
            <div className="row">
              <div className="col-12">
                <div className="table-responsive" style={{ overflow: 'visible', position: 'relative', zIndex: 1 }}>
                  <div className="d-flex">
                    {/* Main Data Table */}
                    <div className="flex-grow-1" style={{ position: 'relative', zIndex: 1 }}>
                      <table className="table table-bordered mb-0" style={{ position: 'relative', zIndex: 1, marginBottom: '0' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '5%' }}>SL No.</th>
                            <th style={{ width: '25%' }}>ITEMS</th>
                            <th style={{ width: '25%' }}>OTHER ITEMS</th>
                            <th style={{ width: '20%' }}>Closing</th>
                            {/* <th style={{ width: '25%' }}>Previous Year</th> */}
                            <th style={{ width: '20%' }}>Status</th>
                            <th style={{ width: '5%' }}>Action</th>
                          </tr>
                          <tr>
                            <th style={{ width: '5%' }}>(1)</th>
                            <th style={{ width: '25%' }}>(2)</th>
                            <th style={{ width: '25%' }}>(3)</th>
                            <th style={{ width: '20%' }}>(4)</th>
                            <th style={{ width: '20%' }}>(5)</th>
                            <th style={{ width: '5%' }}>(6)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {generateTableRows()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Remarks Section */}
        <div className='row'>
          <div className='col-md-6 remarks-section'>

            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={profitLossItems}
              filteredBlockItems={filteredProfitLossItems}
              isLoadingBlockItems={isLoadingProfitLossItems}
              debouncedBlockItemSearch={debouncedBlockItemSearch}
              loadMoreBlockItemData={() => { }}
              hasMoreBlockItems={false}
              isLoadingMoreBlockItems={false}
              selectedItemsFromTable={formData.items.filter(item => item && item.trim() !== '')}
              remarksList={remarksList}
              availableColumns={[3, 4]}
            />

          </div>

          <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>Col-4 and 5 are Mandatory</li>
                  <li>For OTHERS item, Col-3 and Blocks are also Mandatory</li>

                </ul>
              </div>
            </div>
          </div>
        </div>


        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => {
                setFormData({
                  items: Array(100).fill(''),
                  otherItems: Array(100).fill(''),
                  closingData: Array(100).fill(''),
                  status: Array(100).fill('')
                });
                setVisibleRows(1);
                setDropdownStates({});
                setSearchTerms({});
              }}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/balanceSheetData')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                const saved = await handleSaveToAPI();
                if (saved) {
                  navigate('/employmentAndLabourCost');
                }
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Remarks Lists Table */}
      <Remarks__list remarksData={filteredRemarksList} />
    </>
  );
}

export default ProfitAndLossAccount; 